<template>
  <v-card
    ref="playerContainer"
    color="transparent"
    :loading="!audioDownloaded"
    style="z-index: 1;"
    v-bind="$attrs"
    variant="flat"
  >
    <audio
      ref="audio"
      :autoplay="autoplay"
      :src="src"
      @canplaythrough="audioDownloaded = true"
      @durationchange="setDuration"
      @ended="handleAudioEnd"
      @error="$emit('error', $event)"
      @pause="playing = false"
      @play="playing = true"
      @timeupdate="handleTimeUpdate"
    />

    <v-card-text class="pa-2">
      <v-layout wrap>
        <v-row dense>
          <v-col class="align-content-center" cols="auto" order="1">
            <v-avatar class="pa-1 ma-0" color="grey-darken-3" rounded size="125">
              <v-img
                v-if="albumArt"
                :src="albumArt"
              />

              <div v-else class="bg-grey-darken-2 align-content-center" style="width: 100%; height: 100%;">
                <v-icon
                  icon="mdi-radio-tower"
                  size="48"
                />
              </div>
            </v-avatar>
          </v-col>

          <v-col class="align-content-center flex-grow-1" cols="auto" order="3" order-sm="2">
            <v-sheet class="rounded pa-2 fill-height" color="rgba(18,18,18,0.75)" rounded style="min-width: 150px;">
              <div class="font-weight-bold text-capitalize text-h6 text-no-wrap overflow-hidden" :style="`width: ${maxWidth}; text-overflow: ellipsis;`">
                {{ title }}
              </div>

              <div
                class="text-capitalize text-caption text-grey-darken-1"
                v-text="subtitle"
              />

              <div v-if="tags.length > 0" class="mt-2 overflow-hidden" :style="`max-height: 48px; max-width: ${maxWidth};`">
                <v-chip
                  v-for="(tag, index) in tags"
                  :key="index"
                  class="pa-1 mr-1 mb-1 text-capitalize"
                  color="primary"
                  label
                  size="x-small"
                  variant="tonal"
                >
                  {{ tag }}
                </v-chip>
              </div>
            </v-sheet>
          </v-col>

          <v-col class="justify-items-end align-content-center flex-grow-1 flex-sm-grow-0" cols="auto" order="2" order-sm="3">
            <v-sheet class="pa-3 align-content-center fill-height" color="rgba(18,18,18,0.75)" rounded style="width: 100%;">
              <div class="d-flex justify-center align-center mb-1">
                <v-slider
                  v-model="volume"
                  class="ma-0"
                  hide-details
                  max="100"
                  min="0"
                  step="5"
                  thumb-label
                  :value="muted ? 0 : volume"
                  @update:model-value="setVolume"
                >
                  <template #prepend>
                    <v-btn
                      :icon="volumeIcon"
                      size="small"
                      variant="text"
                      @click="muted = !muted"
                    />
                  </template>

                  <template #append>
                    <div class="text-center text-caption" style="min-width: 40px;">
                      {{ volume }}
                    </div>
                  </template>
                </v-slider>
              </div>

              <div
                v-if="src"
                class="d-flex justify-center justify-space-between mt-2"
              >
                <div class="mr-1">
                  <v-btn
                    :disabled="!audioDownloaded || !allowPrevious"
                    icon="mdi-skip-previous"
                    size="small"
                    variant="tonal"
                    @click="$emit('previous-audio')"
                  />
                </div>

                <div class="mr-1 hidden-xs">
                  <v-btn
                    :disabled="!audioDownloaded"
                    icon="mdi-rewind-5"
                    size="small"
                    variant="tonal"
                    @click="forwardSeconds(-5)"
                  />
                </div>

                <div class="mr-1">
                  <v-btn
                    :disabled="!audioDownloaded"
                    :icon="playing ? 'mdi-pause' : 'mdi-play'"
                    size="small"
                    variant="tonal"
                    @click="playing = !playing"
                  />
                </div>

                <div class="mr-1 hidden-xs">
                  <v-btn
                    :disabled="!audioDownloaded"
                    icon="mdi-fast-forward-5"
                    size="small"
                    variant="tonal"
                    @click="forwardSeconds(5)"
                  />
                </div>

                <div class="mr-1">
                  <v-btn
                    :disabled="!audioDownloaded || !allowNext"
                    icon="mdi-skip-next"
                    size="small"
                    variant="tonal"
                    @click="$emit('next-audio')"
                  />
                </div>
              </div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-layout>
    </v-card-text>
  </v-card>

  <div v-if="visualizer" ref="visualiserContainer" style="position: absolute; bottom: -6px; width: 100%; z-index: 0;" />
</template>

<script>
  import AudioMotionAnalyzer from 'audiomotion-analyzer'

  export default {
    name : 'VuetifyAudioPlayer',
    props: {
      src          : { type: String, default: undefined },
      title        : { type: String, default: undefined },
      subtitle     : { type: String, default: undefined },
      tags         : { type: Array, default: undefined },
      autoplay     : { type: Boolean, default: false },
      visualizer   : { type: Boolean, default: false },
      allowPrevious: { type: Boolean, default: false },
      allowNext    : { type: Boolean, default: false },
      albumArt     : { type: String, default: undefined },
      isFavorited  : { type: Boolean, default: false }
    },

    emits: ['next-audio', 'previous-audio', 'error'],
    data () {
      return {
        audioDownloaded: false,
        currentTime    : 0,
        duration       : 0,
        playing        : false,
        volume         : 20,
        muted          : false
      }
    },
    computed: {
      maxWidth () {
        if (this.$vuetify.display.mdAndUp) {
          return '520px'
        }
        if (this.$vuetify.display.smAndUp) {
          return '165px'
        }
        return '280px'
      },

      volumeIcon () {
        if (this.muted) {
          return 'mdi-volume-off'
        } else if (this.volume === 0) {
          return 'mdi-volume-low'
        } else if (this.volume >= 50) {
          return 'mdi-volume-high'
        } else {
          return 'mdi-volume-medium'
        }
      }
    },
    watch: {
      playing (value) {
        if (value) {
          return this.$refs.audio?.play()
        }
        this.$refs.audio?.pause()
      },

      muted (value) {
        if (this.$refs.audio) {
          this.$refs.audio.muted = value
        }
      },

      audioDownloaded (value) {
        if (value && this.autoplay) {
          this.playing = true
        }
      },

      src (value) {
        if (value) {
          this.audioDownloaded = false
          this.playing = false
        }
      },

      volume () {
        this.muted = false
      }
    },
    mounted () {
      if (this.$refs.audio) {
        this.$refs.audio.volume = this.volume / 100
      }
      this.muted = this.$refs.audio?.muted ?? true

      if (this.visualizer) {
        const container = this.$refs.visualiserContainer
        const audioSource = this.$refs.audio
        audioSource.crossOrigin = 'anonymous'
        // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
        const audioMotion = new AudioMotionAnalyzer(container, {
          source       : audioSource,
          height       : 155,
          showScaleX   : false,
          overlay      : true,
          bgAlpha      : 0,
          mode         : 1,
          ledBars      : false,
          lumiBars     : false,
          alphaBars    : true,
          trueLeds     : false,
          channelLayout: 'dual-combined',
          colorMode    : 'gradient',
          gradient     : 'steelblue'
        })
      }
    },
    beforeUnmount () {},
    methods: {
      setVolume (value) {
        this.volume = value
        if (this.$refs.audio) {
          this.$refs.audio.volume = value / 100
        }
      },

      forwardSeconds (seconds) {
        let newTimestamp = this.currentTime + seconds

        if (newTimestamp < 0) {
          newTimestamp = 0
        } else if (newTimestamp > this.duration) {
          newTimestamp = this.duration
        }

        if (this.$refs.audio) {
          this.$refs.audio.currentTime = newTimestamp
        }
      },

      setDuration () {
        this.duration = this.$refs.audio?.duration || 0
      },

      handleTimeUpdate () {
        this.currentTime = this.$refs.audio?.currentTime || 0
      },

      handleAudioEnd () {
        if (this.allowNext) {
          this.$emit('next-audio')
        }
      },

      seek (timePercent) {
        if (this.$refs.audio) {
          this.$refs.audio.currentTime = this.$refs.audio.duration * (timePercent / 1_000_000)
        }
      }
    }
  }
</script>

<style scoped>

</style>
